// Hook do pobierania listy zadan konkretnego klienta - GET /sales-crm/clients/{clientId}/tasks.
// Uzywany w widoku "Terminarz" w kontekscie klienta (/trader/clients/:id/calendar).
// Filtrowanie po zakresie dat realizowane przez `filter[and][N][and][0][field|op|value]`.

import qs from 'query-string';
import { axios } from 'api';
import { useQuery, UseQueryOptions } from 'react-query';
import { ICommandResponseError as IError } from 'api/types';
import { IGetSalesCrmTasksResponse } from 'api/endpoints/useGetSalesCrmTasks';

export interface IGetSalesCrmClientTasksParams {
  page?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
  type?: string;
  status?: string;
}

const buildRangeFilter = (dateFrom?: string, dateTo?: string): Record<string, string> => {
  const flat: Record<string, string> = {};
  const conditions: { op: 'gte' | 'lte'; value: string }[] = [];

  if (dateFrom) conditions.push({ op: 'gte', value: dateFrom });
  if (dateTo) conditions.push({ op: 'lte', value: dateTo });

  conditions.forEach((cond, idx) => {
    const prefix = `filter[and][${idx}][and][0]`;
    flat[`${prefix}[field]`] = 'due_date';
    flat[`${prefix}[op]`] = cond.op;
    flat[`${prefix}[value]`] = cond.value;
  });

  return flat;
};

const getSalesCrmClientTasks = (
  clientId: number,
  params: IGetSalesCrmClientTasksParams
): Promise<IGetSalesCrmTasksResponse> => {
  const { date_from, date_to, page, limit, type, status } = params;

  const queryParams: Record<string, string | number | undefined> = {
    page,
    limit,
    type,
    status,
    ...buildRangeFilter(date_from, date_to)
  };

  const queryString = qs.stringify(queryParams, {
    skipEmptyString: true,
    skipNull: true
  });

  return axios.get(
    `/sales-crm/clients/${clientId}/tasks${queryString ? `?${queryString}` : ''}`
  );
};

export const useGetSalesCrmClientTasks = (
  clientId: number | null | undefined,
  params: IGetSalesCrmClientTasksParams = {},
  options?: UseQueryOptions<IGetSalesCrmTasksResponse, IError>
) =>
  useQuery<IGetSalesCrmTasksResponse, IError>(
    ['sales-crm', 'client-tasks', clientId, params],
    () => getSalesCrmClientTasks(clientId as number, params),
    {
      ...options,
      enabled: options?.enabled !== false && clientId != null && !Number.isNaN(clientId)
    }
  );
